import React from 'react';
import { SystemCheckItem as SystemCheckItemType, SystemCheckId } from '../types';

interface SystemCheckItemProps {
  item: SystemCheckItemType;
  onRetry?: (id: SystemCheckId) => void;
}

const getIcon = (id: string) => {
  switch (id) {
    case 'browser':
    case 'secureContext': return '🔒';
    case 'mediaDevices': return '⚙️';
    case 'microphone':
    case 'microphoneSignal': return '🎙️';
    case 'camera':
    case 'cameraSignal': return '📹';
    case 'speaker': return '🔊';
    case 'network': return '🌐';
    default: return '✨';
  }
};

const StatusBadge: React.FC<{ status: string; required?: boolean; message?: string }> = ({ status, required, message }) => {
  const base: React.CSSProperties = {
    borderRadius: '20px',
    padding: '2px 10px',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.03em',
    whiteSpace: 'nowrap',
  };

  const isOptional = required === false ||
    message?.toLowerCase().includes('not required') ||
    message?.toLowerCase().includes('skipped') ||
    message?.toLowerCase().includes('optional');

  if (isOptional) {
    if (status === 'passed') {
      return (
        <span style={{
          ...base,
          background: 'rgba(52,211,153,0.10)',
          color: '#34D399',
          border: '1px solid rgba(52,211,153,0.20)',
        }}>
          ✓ OPTIONAL
        </span>
      );
    }
    return (
      <span style={{
        ...base,
        background: 'rgba(148, 163, 184, 0.12)',
        color: '#94A3B8',
        border: '1px solid rgba(148, 163, 184, 0.25)',
      }}>
        OPTIONAL
      </span>
    );
  }

  switch (status) {
    case 'checking':
      return (
        <span style={{ ...base, background: 'rgba(99,102,241,0.12)', color: '#818CF8', border: '1px solid rgba(99,102,241,0.25)', display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#818CF8', display: 'inline-block', animation: 'pulse 1s infinite' }} />
          CHECKING...
        </span>
      );
    case 'passed':
      return <span style={{ ...base, background: 'rgba(52,211,153,0.10)', color: '#34D399', border: '1px solid rgba(52,211,153,0.20)' }}>✓ PASSED</span>;
    case 'warning':
      return <span style={{ ...base, background: 'rgba(252,211,77,0.10)', color: '#FCD34D', border: '1px solid rgba(252,211,77,0.20)' }}>⚠ WARNING</span>;
    case 'failed':
      return <span style={{ ...base, background: 'rgba(248,113,113,0.10)', color: '#F87171', border: '1px solid rgba(248,113,113,0.20)' }}>✕ REQUIRED</span>;
    default:
      return <span style={{ ...base, background: 'rgba(100,116,139,0.15)', color: '#94A3B8', border: '1px solid rgba(100,116,139,0.20)' }}>PENDING</span>;
  }
};

export const SystemCheckItemView: React.FC<SystemCheckItemProps> = ({ item, onRetry }) => {
  return (
    <div
      role="listitem"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderRadius: '10px',
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.07)',
        padding: '10px 12px',
        gap: '8px',
      }}
    >
      {/* Left: icon + text */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: '18px', marginTop: '1px', userSelect: 'none', flexShrink: 0 }}>
          {getIcon(item.id)}
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <p style={{ margin: 0, fontSize: '12px', fontWeight: 700, color: '#F1F5F9' }}>{item.label}</p>
            {item.required ? (
              <span style={{ fontSize: '10px', color: '#F87171' }}>*required</span>
            ) : (
              <span style={{ fontSize: '10px', color: '#64748B' }}>(optional)</span>
            )}
          </div>
          <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#64748B', lineHeight: 1.4 }}>
            {item.details || item.description}
          </p>
          {item.message && (
            <p style={{
              margin: '3px 0 0',
              fontSize: '11px',
              fontWeight: 500,
              color: item.status === 'failed' ? '#F87171' : item.status === 'warning' ? '#FCD34D' : '#CBD5E1',
            }}>
              {item.message}
            </p>
          )}
        </div>
      </div>

      {/* Right: badge + retry */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
        <StatusBadge status={item.status} required={item.required} message={item.message} />
        {(item.status === 'failed' || item.status === 'warning') && onRetry && (
          <button
            type="button"
            onClick={() => onRetry(item.id)}
            title="Retry this check"
            style={{
              background: 'rgba(99,102,241,0.10)',
              border: '1px solid rgba(99,102,241,0.25)',
              borderRadius: '6px',
              color: '#818CF8',
              fontSize: '11px',
              fontWeight: 600,
              padding: '3px 10px',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
};
